import { Component, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import Swal from 'sweetalert2';
import { VehicleService } from './vehicle.service';
import { VehicleDialogComponent } from './vehicle-dialog/vehicle-dialog.component';
import { AssignVehicleDialogComponent } from './assign-vehicle-dialog/assign-vehicle-dialog.component';

@Component({
  selector: 'app-vehicle',
  templateUrl: './vehicle.component.html',
  styleUrls: ['./vehicle.component.scss']
})
export class VehicleComponent implements OnInit {
  displayedColumns: string[] = [
    'plateNumber',
    'model',
    'type',
    'capacity',
    'riderName',
    'startMeter',
    'startFuelLevel',
    'endMeter',
    'endFuelLevel',
    'fuelUsed',
    'distanceToday',
    'isActive',
    'meterActions',
    'actions'
  ];

  dataSource = new MatTableDataSource<any>([]);
  loading = false;
  analytics: any = null;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private vehicleService: VehicleService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadPageData();
  }

  loadPageData(): void {
    this.loading = true;
    forkJoin({
      vehicles: this.vehicleService.getVehicles(),
      analytics: this.vehicleService.getMeterAnalytics()
    }).subscribe({
      next: ({ vehicles, analytics }) => {
        this.dataSource.data = vehicles || [];
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.analytics = analytics;
        this.loading = false;
      },
      error: (err) => {
        console.error('[VehicleComponent] ❌ Failed to load page data:', err);
        Swal.fire('Error', 'Failed to load vehicles and analytics', 'error');
        this.loading = false;
      }
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(VehicleDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'app-modal-panel',
      backdropClass: 'app-modal-backdrop'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadPageData();
    });
  }

  openEditDialog(vehicle: any): void {
    const dialogRef = this.dialog.open(VehicleDialogComponent, {
      width: '600px',
      disableClose: true,
      maxWidth: '95vw',
      panelClass: 'app-modal-panel',
      backdropClass: 'app-modal-backdrop',
      data: vehicle
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadPageData();
    });
  }

  openAssignDialog(vehicle: any): void {
    const dialogRef = this.dialog.open(AssignVehicleDialogComponent, {
      width: '480px',
      disableClose: true,
      maxWidth: '95vw',
      panelClass: 'app-modal-panel',
      backdropClass: 'app-modal-backdrop',
      data: {
        vehicleId: vehicle.id,
        currentRiderId: vehicle.riderId,
        plateNumber: vehicle.plateNumber
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadPageData();
    });
  }

  deleteVehicle(vehicle: any): void {
    Swal.fire({
      title: 'Delete Vehicle?',
      text: `Are you sure you want to delete ${vehicle.plateNumber}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it'
    }).then((result) => {
      if (result.isConfirmed) {
        this.vehicleService.deleteVehicle(vehicle.id).subscribe({
          next: () => {
            Swal.fire('Deleted', 'Vehicle deleted successfully', 'success');
            this.loadPageData();
          },
          error: (err) => {
            console.error('[VehicleComponent] ❌ Error deleting vehicle:', err);
            Swal.fire('Error', 'Failed to delete vehicle', 'error');
          }
        });
      }
    });
  }

  async openWork(vehicle: any): Promise<void> {
    const result = await Swal.fire({
      title: `Open Work: ${vehicle.plateNumber}`,
      html: `
        <input id="swal-start-meter" class="swal2-input" placeholder="Start meter reading" type="number" step="0.01" min="0">
        <input id="swal-start-fuel" class="swal2-input" placeholder="Start fuel level (%)" type="number" step="0.01" min="0" max="100">
      `,
      icon: 'warning',
      text: 'Please confirm this meter reading carefully before submitting.',
      showCancelButton: true,
      confirmButtonText: 'Submit Start Meter',
      preConfirm: () => {
        const meter = Number((document.getElementById('swal-start-meter') as HTMLInputElement)?.value);
        const fuel = Number((document.getElementById('swal-start-fuel') as HTMLInputElement)?.value);
        if (Number.isNaN(meter) || meter < 0) {
          Swal.showValidationMessage('Enter a valid start meter value.');
          return false;
        }
        if (Number.isNaN(fuel) || fuel < 0 || fuel > 100) {
          Swal.showValidationMessage('Enter a valid start fuel level between 0 and 100.');
          return false;
        }
        return { meter, fuel };
      }
    });

    if (!result.isConfirmed) return;

    this.vehicleService.openWork(vehicle.id, result.value.meter, result.value.fuel).subscribe({
      next: () => {
        Swal.fire('Saved', 'Start-of-day meter captured.', 'success');
        this.loadPageData();
      },
      error: (err) => {
        Swal.fire('Error', err?.error?.message || 'Failed to submit start meter.', 'error');
      }
    });
  }

  async closeWork(vehicle: any): Promise<void> {
    const result = await Swal.fire({
      title: `Close Work: ${vehicle.plateNumber}`,
      html: `
        <input id="swal-end-meter" class="swal2-input" placeholder="End meter reading" type="number" step="0.01" min="0">
        <input id="swal-end-fuel" class="swal2-input" placeholder="End fuel level (%)" type="number" step="0.01" min="0" max="100">
      `,
      icon: 'warning',
      text: 'Please confirm this meter reading carefully before submitting.',
      showCancelButton: true,
      confirmButtonText: 'Submit End Meter',
      preConfirm: () => {
        const meter = Number((document.getElementById('swal-end-meter') as HTMLInputElement)?.value);
        const fuel = Number((document.getElementById('swal-end-fuel') as HTMLInputElement)?.value);
        if (Number.isNaN(meter) || meter < 0) {
          Swal.showValidationMessage('Enter a valid end meter value.');
          return false;
        }
        if (Number.isNaN(fuel) || fuel < 0 || fuel > 100) {
          Swal.showValidationMessage('Enter a valid end fuel level between 0 and 100.');
          return false;
        }
        return { meter, fuel };
      }
    });

    if (!result.isConfirmed) return;

    this.vehicleService.closeWork(vehicle.id, result.value.meter, result.value.fuel).subscribe({
      next: () => {
        Swal.fire('Saved', 'End-of-day meter captured.', 'success');
        this.loadPageData();
      },
      error: (err) => {
        Swal.fire('Error', err?.error?.message || 'Failed to submit end meter.', 'error');
      }
    });
  }

  async editMeter(vehicle: any): Promise<void> {
    if (!vehicle.meterLogId) {
      Swal.fire('Not Available', 'No meter entry found for this vehicle today.', 'info');
      return;
    }

    const now = Date.now();
    const openedAt = vehicle.workOpenedAt ? new Date(vehicle.workOpenedAt).getTime() : now;
    const minutesFromOpen = Math.floor((now - openedAt) / 60000);
    if (minutesFromOpen > 15) {
      Swal.fire('Edit Locked', 'Edit is only allowed within 15 minutes after insert.', 'warning');
      return;
    }

    const formHtml = `
      <input id="swal-start" class="swal2-input" placeholder="Start meter" type="number" step="0.01" min="0" value="${vehicle.startMeter ?? ''}">
      <input id="swal-end" class="swal2-input" placeholder="End meter" type="number" step="0.01" min="0" value="${vehicle.endMeter ?? ''}">
      <input id="swal-start-fuel-edit" class="swal2-input" placeholder="Start fuel level (%)" type="number" step="0.01" min="0" max="100" value="${vehicle.startFuelLevel ?? ''}">
      <input id="swal-end-fuel-edit" class="swal2-input" placeholder="End fuel level (%)" type="number" step="0.01" min="0" max="100" value="${vehicle.endFuelLevel ?? ''}">
    `;

    const result = await Swal.fire({
      title: `Edit Meter: ${vehicle.plateNumber}`,
      html: formHtml,
      icon: 'warning',
      text: 'Warning: edits are only allowed within 15 minutes after insert.',
      showCancelButton: true,
      confirmButtonText: 'Update Meter',
      preConfirm: () => {
        const startRaw = (document.getElementById('swal-start') as HTMLInputElement)?.value;
        const endRaw = (document.getElementById('swal-end') as HTMLInputElement)?.value;
        const startFuelRaw = (document.getElementById('swal-start-fuel-edit') as HTMLInputElement)?.value;
        const endFuelRaw = (document.getElementById('swal-end-fuel-edit') as HTMLInputElement)?.value;

        const payload: any = {};
        if (startRaw !== '') payload.startMeter = Number(startRaw);
        if (endRaw !== '') payload.endMeter = Number(endRaw);
        if (startFuelRaw !== '') payload.startFuelLevel = Number(startFuelRaw);
        if (endFuelRaw !== '') payload.endFuelLevel = Number(endFuelRaw);

        if (payload.startMeter == null && payload.endMeter == null && payload.startFuelLevel == null && payload.endFuelLevel == null) {
          Swal.showValidationMessage('Enter meter or fuel fields to update.');
          return false;
        }

        if (payload.startMeter != null && payload.startMeter < 0) {
          Swal.showValidationMessage('Start meter cannot be negative.');
          return false;
        }

        if (payload.endMeter != null && payload.endMeter < 0) {
          Swal.showValidationMessage('End meter cannot be negative.');
          return false;
        }
        if (payload.startFuelLevel != null && (payload.startFuelLevel < 0 || payload.startFuelLevel > 100)) {
          Swal.showValidationMessage('Start fuel level must be between 0 and 100.');
          return false;
        }
        if (payload.endFuelLevel != null && (payload.endFuelLevel < 0 || payload.endFuelLevel > 100)) {
          Swal.showValidationMessage('End fuel level must be between 0 and 100.');
          return false;
        }

        return payload;
      }
    });

    if (!result.isConfirmed) return;

    this.vehicleService.editMeterLog(vehicle.meterLogId, result.value).subscribe({
      next: () => {
        Swal.fire('Updated', 'Meter entry updated.', 'success');
        this.loadPageData();
      },
      error: (err) => {
        Swal.fire('Error', err?.error?.message || 'Failed to edit meter entry.', 'error');
      }
    });
  }
}
